import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { JaaSMeeting } from '@jitsi/react-sdk';
import { Copy, Check, PhoneOff, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

interface TeleconsultaToken {
  jwt: string;
  roomName: string;
  appId: string;
  patientLink: string;
}

export function TeleconsultaPage() {
  const { citaId } = useParams<{ citaId: string }>();
  const [tokenData, setTokenData] = useState<TeleconsultaToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    if (!citaId) return;
    api.get<TeleconsultaToken>(`/api/teleconsultas/token/${citaId}`)
      .then(r => setTokenData(r.data))
      .catch(err => {
        setError(getErrorMessage(err, 'Error al obtener acceso a la videollamada'));
      })
      .finally(() => setLoading(false));
  }, [citaId]);

  function handleCopy() {
    if (!tokenData?.patientLink) return;
    navigator.clipboard.writeText(tokenData.patientLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-blue-400" />
          <p className="text-sm text-gray-400">Preparando videollamada...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white px-6">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <PhoneOff size={24} className="text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">No se pudo iniciar la llamada</h2>
          <p className="text-sm text-gray-400 mb-6">{error}</p>
          {error.includes('JAAS_PRIVATE_KEY') && (
            <p className="text-xs text-yellow-400 bg-yellow-900/30 border border-yellow-700/40 rounded-lg px-4 py-3">
              Obtén tu clave RSA en{' '}
              <a
                href="https://jaas.8x8.vc/#/apikeys"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                jaas.8x8.vc/#/apikeys
              </a>{' '}
              y agrégala en <code className="font-mono">JAAS_PRIVATE_KEY</code> del archivo <code className="font-mono">.env</code>.
            </p>
          )}
          <button
            onClick={() => window.close()}
            className="mt-5 text-sm text-gray-500 hover:text-gray-300 transition-colors"
          >
            Cerrar ventana
          </button>
        </div>
      </div>
    );
  }

  if (ended) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <PhoneOff size={24} className="text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Consulta finalizada</h2>
          <p className="text-sm text-gray-500 mb-5">La videollamada ha terminado.</p>
          <button
            onClick={() => window.close()}
            className="text-sm bg-gray-800 hover:bg-gray-700 px-5 py-2 rounded-xl transition-colors"
          >
            Cerrar ventana
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-950">
      {/* Barra superior */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-900 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-sm font-medium text-white">Teleconsulta en curso</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 hidden sm:block">Link para el paciente:</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-200 px-3 py-1.5 rounded-lg transition-colors border border-gray-700"
          >
            {copied ? (
              <>
                <Check size={12} className="text-green-400" />
                <span className="text-green-400">¡Copiado!</span>
              </>
            ) : (
              <>
                <Copy size={12} />
                Copiar link del paciente
              </>
            )}
          </button>
        </div>
      </div>

      {/* JaaS Meeting */}
      <div className="flex-1 overflow-hidden">
        {tokenData && (
          <JaaSMeeting
            appId={tokenData.appId}
            roomName={tokenData.roomName}
            jwt={tokenData.jwt}
            configOverwrite={{
              startWithAudioMuted: false,
              startWithVideoMuted: false,
              disableThirdPartyRequests: true,
              prejoinPageEnabled: false,
            }}
            interfaceConfigOverwrite={{
              MOBILE_APP_PROMO: false,
              SHOW_JITSI_WATERMARK: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
            }}
            onReadyToClose={() => setEnded(true)}
            getIFrameRef={(iframeRef) => {
              iframeRef.style.width = '100%';
              iframeRef.style.height = '100%';
              iframeRef.style.border = 'none';
            }}
          />
        )}
      </div>
    </div>
  );
}
